import React, { Fragment, useEffect, useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { REGLES_CONSERVATION, REGLE_CONSERVATION } from '../../auth/urlParameters';
import { ToastConfirm, ToastError } from '../../../ui/AlertesUi';
import './ArchiveStyles.css';
import './ArchiveCreate.css';

const supportOptions = [
  { value: 'papier', label: 'Papier' },
  { value: 'pdfa', label: 'PDF/A' },
  { value: 'image', label: 'Image' },
  { value: 'ged', label: 'Base GED' },
];

const destinationOptions = [
  { value: 'conservation_permanente', label: 'Conservation permanente' },
  { value: 'destruction_securisee', label: 'Destruction sécurisée' },
  { value: 'transfert', label: 'Transfert vers un autre service ou organisme' },
];

const defaultForm = {
  code: '',
  regle: '',
  description: '',
  retentionDurationYears: '',
  isUnlimited: false,
  support: [],
  finalDestination: '',
  responsable: '',
  retentionTrigger: 'deposit',
  triggerEventLabel: '',
};

const RegleConservation = () => {
  const session = useSelector((state) => state.sessionAuth);
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(defaultForm);
  const [loading, setLoading] = useState(false);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const isEdit = Boolean(id);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    axios
      .get(REGLE_CONSERVATION(id), authHeaders)
      .then((res) => {
        const data = res.data || {};
        setForm({
          code: data.code || '',
          regle: data.regle || '',
          description: data.description || '',
          retentionDurationYears: data.retentionDurationYears ? String(data.retentionDurationYears) : '',
          isUnlimited: Boolean(data.isUnlimited),
          support: Array.isArray(data.support) ? data.support : [],
          finalDestination: data.finalDestination || '',
          responsable: data.responsable || '',
          retentionTrigger: data.retentionTrigger || 'deposit',
          triggerEventLabel: data.triggerEventLabel || '',
        });
      })
      .catch(() => ToastError('Impossible de charger la règle'))
      .finally(() => setLoading(false));
  }, [authHeaders, id, isEdit]);

  const toggleSupport = (value) => {
    setForm((prev) => {
      const exists = prev.support.includes(value);
      return {
        ...prev,
        support: exists ? prev.support.filter((item) => item !== value) : [...prev.support, value],
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!/^[A-Z0-9][A-Z0-9._-]{0,63}$/.test(form.code.trim().toUpperCase())) {
      ToastError('Saisissez un code de 1 à 64 caractères : lettres, chiffres, point, tiret ou soulignement.');
      return;
    }
    if (!form.regle.trim()) {
      ToastError('La règle de conservation est requise');
      return;
    }
    if (!form.responsable.trim()) {
      ToastError('Le responsable est requis');
      return;
    }
    if (!form.description.trim()) {
      ToastError('La description de la règle est requise.');
      return;
    }
    if (form.retentionTrigger === 'event' && !form.triggerEventLabel.trim()) {
      ToastError('Précisez l’événement qui démarre la conservation.');
      return;
    }
    if (form.isUnlimited && form.finalDestination === 'destruction_securisee') {
      ToastError('Une conservation illimitée ne peut pas prévoir une destruction.');
      return;
    }
    if (!form.finalDestination) {
      ToastError('Choisissez un sort final.');
      return;
    }
    if (!form.isUnlimited) {
      const years = Number(form.retentionDurationYears);
      if (!years || years <= 0) {
        ToastError('La durée doit être un nombre positif ou illimitée');
        return;
      }
    }

    const payload = {
      code: form.code.trim().toUpperCase(),
      regle: form.regle.trim(),
      description: form.description.trim(),
      isUnlimited: form.isUnlimited,
      support: form.support,
      finalDestination: form.finalDestination,
      responsable: form.responsable.trim(),
      retentionTrigger: form.retentionTrigger,
      triggerEventLabel: form.retentionTrigger === 'event' ? form.triggerEventLabel.trim() : null,
    };

    if (!form.isUnlimited) {
      payload.retentionDurationYears = Number(form.retentionDurationYears);
    } else {
      payload.retentionDurationYears = null;
    }

    setLoading(true);
    const request = isEdit
      ? axios.patch(REGLE_CONSERVATION(id), payload, authHeaders)
      : axios.post(REGLES_CONSERVATION(), payload, authHeaders);

    request
      .then(() => {
        ToastConfirm(isEdit ? 'Règle mise à jour' : 'Règle créée');
        navigate('/apps/in/archives/regles-conservation');
      })
      .catch((err) => {
        ToastError(err.response?.data?.message || 'Erreur d\'enregistrement de la règle');
      })
      .finally(() => setLoading(false));
  };

  return (
    <Fragment>
      <Breadcrumb title="Archives" submenu="Règle de conservation" />
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-5 col-lg-4 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-7 col-lg-8 col-xl-9 retention-rule-page">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h4 className="htitle mb-0">{isEdit ? 'Modifier une règle de conservation' : 'Créer une règle de conservation'}</h4>
                  <button
                    className="btn btn-outline-secondary"
                    type="button"
                    onClick={() => navigate('/apps/in/archives/regles-conservation')}
                  >
                    Retour à la liste
                  </button>
              </div>

              <form onSubmit={handleSubmit} className="archive-create-form">
                <section className="archive-create-section">
                  <div className="archive-create-section-heading">
                    <span className="archive-create-step">1</span>
                    <div><h5>Identification</h5><p>Nommez la règle pour la retrouver et comprendre son périmètre.</p></div>
                  </div>
                  <div className="archive-create-section-body">
                    <div className="row">
                      <div className="col-md-6 form-group">
                        <label htmlFor="rule-code">Code de la règle</label>
                        <input id="rule-code" className="form-control" maxLength={64} value={form.code}
                          onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                          placeholder="Ex. FIN-FACT-10" required />
                        <small className="form-text text-muted">Identifiant court de la règle, unique parmi les règles en vigueur.</small>
                      </div>
                      <div className="col-md-6 form-group">
                        <label htmlFor="rule-title">Titre de la règle de conservation</label>
                        <input id="rule-title" className="form-control" value={form.regle}
                          onChange={(e) => setForm((prev) => ({ ...prev, regle: e.target.value }))} required />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-12 form-group">
                        <label htmlFor="rule-description">Description</label>
                        <textarea id="rule-description" className="form-control" rows={3} value={form.description}
                          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                          placeholder="Précisez les documents et la finalité de cette règle." required />
                      </div>
                    </div>
                  </div>
                </section>

                <section className="archive-create-section">
                  <div className="archive-create-section-heading">
                    <span className="archive-create-step">2</span>
                    <div><h5>Durée de conservation</h5><p>Choisissez à partir de quand la durée est calculée.</p></div>
                  </div>
                  <div className="archive-create-section-body">
                    <div className="row">
                      <div className="col-md-6 form-group">
                        <label htmlFor="rule-trigger">Début de la durée de conservation</label>
                        <select id="rule-trigger" className="form-control" value={form.retentionTrigger} onChange={(e) => setForm((prev) => ({ ...prev, retentionTrigger: e.target.value }))}>
                          <option value="deposit">Date de versement dans Syidocs</option>
                          <option value="event">Date d’un événement métier à renseigner au dépôt</option>
                        </select>
                      </div>
                      {form.retentionTrigger === 'event' && <div className="col-md-6 form-group">
                        <label htmlFor="rule-event">Événement déclencheur</label>
                        <input id="rule-event" className="form-control" value={form.triggerEventLabel} onChange={(e) => setForm((prev) => ({ ...prev, triggerEventLabel: e.target.value }))} placeholder="Ex. clôture du dossier" required />
                      </div>}
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Durée de conservation (années)</label>
                          <input
                            type="number"
                            min="1"
                            className="form-control"
                            value={form.retentionDurationYears}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                retentionDurationYears: e.target.value,
                              }))
                            }
                            disabled={form.isUnlimited}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group retention-unlimited-field">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.isUnlimited}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  isUnlimited: e.target.checked,
                                  retentionDurationYears: e.target.checked ? '' : prev.retentionDurationYears,
                                }))
                              }
                              id="regleUnlimited"
                            />
                            <label className="form-check-label" htmlFor="regleUnlimited">
                              Conservation illimitée
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="archive-create-section">
                  <div className="archive-create-section-heading">
                    <span className="archive-create-step">3</span>
                    <div><h5>Application et sort final</h5><p>Précisez les supports, la destination et le service responsable.</p></div>
                  </div>
                  <div className="archive-create-section-body">
                    <div className="form-group">
                      <label>Support, format et système</label>
                      <small className="form-text text-muted mb-2">Papier : support physique · PDF/A et image : formats · Base GED : système de stockage.</small>
                      <div className="retention-support-options">
                        {supportOptions.map((option) => (
                          <div className="form-check" key={option.value}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={form.support.includes(option.value)}
                              onChange={() => toggleSupport(option.value)}
                              id={`support-${option.value}`}
                            />
                            <label className="form-check-label" htmlFor={`support-${option.value}`}>
                              {option.label}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Sort final</label>
                          <select
                            className="form-control"
                            value={form.finalDestination}
                            onChange={(e) =>
                              setForm((prev) => ({ ...prev, finalDestination: e.target.value }))
                            }
                          >
                            <option value="">Choisir un sort final</option>
                            {destinationOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Responsable : direction ou service chargé de l’application</label>
                          <input
                            className="form-control"
                            value={form.responsable}
                            onChange={(e) => setForm((prev) => ({ ...prev, responsable: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="archive-create-actions archive-form-actions">
                  <button className="btn btn-outline-secondary" type="button" onClick={() => navigate('/apps/in/archives/regles-conservation')}>Annuler</button>
                  <button className="btn btn-primary" type="submit" disabled={loading}>
                    {loading ? 'Enregistrement...' : isEdit ? 'Enregistrer les modifications' : 'Créer la règle'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default RegleConservation;
